import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import createOpenGraphTags from '@repo/utils/createOpenGraphTags';
import Layout from '@components/layout';
import GearTagLine from '@components/GearTagLine';

import BreadcrumbWithWrapper from '@repo/ui-components/BreadcrumbWithWrapper';
import BlogArticle from '@repo/ui-components/articles/BlogArticle';
import { getLocalizedPagePath } from '@repo/gatsby-localization-helpers';
import { useSportdogInternationalContext } from '@util/static-translations';
import getInternalLink from '@utils/getInternalLink';

function TrainingTipsDetailsTemplate({ data, pageContext }) {
  const { sanityBlogPost } = data;
  const { linkHreflangValues } = pageContext;

  const { SEOMetadata, title, description, image, author } = sanityBlogPost;

  const { tt, locale } = useSportdogInternationalContext();

  const breadcrumbs = [
    {
      text: tt(s => s.breadcrumbsCommon.home),
      link: '/',
    },
    {
      text: tt(s => s.breadcrumbsCommon.trainingTips),
      link: '/hunting-training-tips',
    },
    {
      text: title,
    },
  ];

  const authorLink = author?.slug
    ? `/about-us/pro-staff/${author.slug.current}`
    : '/about-us/pro-staff';

  return (
    <Layout
      headTagData={{
        title: SEOMetadata?.title || title,
        description: SEOMetadata?.description || description,
        canonicalUrl: SEOMetadata?.canonicalUrl,
        noIndex: SEOMetadata?.noIndex,
        noFollow: SEOMetadata?.noFollow,
        meta: createOpenGraphTags(
          {
            title: SEOMetadata?.ogTitle || SEOMetadata?.title || title,
            description:
              SEOMetadata?.ogDescription ||
              SEOMetadata?.description ||
              description,
            url:
              SEOMetadata?.ogUrl ||
              SEOMetadata?.canonicalUrl ||
              (typeof window !== 'undefined' && window?.location?.pathname) ||
              getLocalizedPagePath(`/`, locale),
            image: SEOMetadata?.ogImage || image || '',
          },
          'sportdog'
        ),
      }}
      linkHreflangValues={linkHreflangValues}
    >
      <BreadcrumbWithWrapper elements={breadcrumbs} />
      <BlogArticle
        getInternalLink={getInternalLink}
        article={sanityBlogPost}
        blogBaseUrl="hunting-training-tips"
        authorLink={authorLink}
      />
      <GearTagLine />
    </Layout>
  );
}

export const query = graphql`
  query ArticlePageQuery($_id: String) {
    sanityBlogPost(_id: { eq: $_id }) {
      title
      description
      publishedDate
      body: _rawLocalizedBody(resolveReferences: { maxDepth: 5 })
      image {
        asset: _rawAsset(resolveReferences: { maxDepth: 3 })
        crop: _rawCrop(resolveReferences: { maxDepth: 3 })
        hotspot: _rawHotspot(resolveReferences: { maxDepth: 3 })
        alt
      }
      author: localizedAuthorValue {
        title
        location
        description
        language
        slug {
          current
        }
        image {
          asset: _rawAsset(resolveReferences: { maxDepth: 3 })
          crop: _rawCrop(resolveReferences: { maxDepth: 3 })
          hotspot: _rawHotspot(resolveReferences: { maxDepth: 3 })
          alt
        }
      }
      slug {
        current
      }
      categories: localizedCategoriesValue {
        title
        slug {
          current
        }
      }
      SEOMetadata {
        title
        description
        canonicalUrl
        noIndex
        noFollow
        ogTitle
        ogDescription
        ogUrl
        ogImage {
          asset {
            url
          }
        }
      }
      relatedArticles: localizedRelatedArticlesValue {
        title
        image {
          asset: _rawAsset(resolveReferences: { maxDepth: 3 })
          crop: _rawCrop(resolveReferences: { maxDepth: 3 })
          hotspot: _rawHotspot(resolveReferences: { maxDepth: 3 })
          alt
        }
        author: localizedAuthorValue {
          title
        }
        slug {
          current
        }
        description
      }
      relatedProducts: localizedRelatedProductsValue {
        features
        store {
          id
          title
          handle
          previewImageUrl
          productType
          priceRangeV2 {
            minVariantPrice {
              amount
            }
            maxVariantPrice {
              amount
            }
          }
          variants {
            shopifyId: Id
            sku
            price
            compareAtPrice
            image: media {
              image {
                src
              }
            }
          }
          media {
            ... on SanityProductMediaImage {
              image {
                altText
                src
              }
            }
          }
          vendor
        }
      }
    }
  }
`;

TrainingTipsDetailsTemplate.propTypes = {
  data: PropTypes.shape({
    sanityBlogPost: PropTypes.shape({
      body: PropTypes.arrayOf(PropTypes.shape({})),
      image: PropTypes.shape({
        asset: PropTypes.shape({
          url: PropTypes.string,
        }),
      }),
      relatedArticles: PropTypes.shape({}),
      relatedProducts: PropTypes.shape({}),
      SEOMetadata: PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
        canonicalUrl: PropTypes.string,
        noIndex: PropTypes.bool,
        noFollow: PropTypes.bool,
        ogTitle: PropTypes.string,
        ogDescription: PropTypes.string,
        ogUrl: PropTypes.string,
        ogImage: PropTypes.shape({
          asset: PropTypes.shape({
            url: PropTypes.string,
          }),
        }),
      }),
      author: PropTypes.shape({
        slug: PropTypes.shape({
          current: PropTypes.string,
        }),
        title: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
        personCategory: PropTypes.shape({
          slug: PropTypes.shape({
            current: PropTypes.string,
          }),
        }).isRequired,
        image: PropTypes.shape({
          altText: PropTypes.string,
          asset: PropTypes.shape({
            url: PropTypes.string,
            altText: PropTypes.string,
          }),
        }).isRequired,
        location: PropTypes.string,
        background: PropTypes.shape({
          background: PropTypes.string,
        }),
      }),
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      breadcrumbs: PropTypes.arrayOf(
        PropTypes.shape({
          text: PropTypes.string.isRequired,
          link: PropTypes.string,
        })
      ),
    }),
  }).isRequired,

  pageContext: PropTypes.shape({
    linkHreflangValues: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
};
export default TrainingTipsDetailsTemplate;
